import React, { Component } from 'react';
import classNames from 'classnames';
import { withContext } from '../utils/StateProvider';
import { statBonus, characterAC, meleeHitBonus, missileHitBonus } from '../utils/combat/rules';
import { SPELLS, spellCost } from '../utils/combat/spells';
import { SLOTS, SLOT_NAMES, itemName, itemSummary } from '../utils/items';
import { BACKPACK_SIZE } from '../utils/party';
import { levelProgress, xpToNext, hasExperienceToTrain } from '../utils/leveling';

const STATS = [
  ['might', 'Might'],
  ['intellect', 'Intellect'],
  ['personality', 'Personality'],
  ['endurance', 'Endurance'],
  ['speed', 'Speed'],
  ['accuracy', 'Accuracy'],
  ['luck', 'Luck'],
];

const TABS = [['stats', 'Stats'], ['gear', 'Gear'], ['spells', 'Spells']];

const signed = (n) => (n >= 0 ? '+' + n : String(n));

// "1d10+3" style damage, with the range it can roll
function damageText([count, sides], bonus) {
  const min = Math.max(1, count + bonus);
  const max = Math.max(1, count * sides + bonus);
  return `${count}d${sides}${bonus ? signed(bonus) : ''} (${min}-${max})`;
}

// A full-view overlay showing one character in detail. Opened from the
// CHARACTER button; clicking a portrait switches character, Esc closes.
class CharacterSheet extends Component {
  state = { tab: 'stats' };

  componentDidMount() {
    window.addEventListener('keydown', this.handleKeydown);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeydown);
  }

  handleKeydown = (e) => {
    const { characterSheet, party, openCharacterSheet, closeCharacterSheet } = this.props.context;
    if (characterSheet === null) return;
    if (e.key === 'Escape') {
      closeCharacterSheet();
      return;
    }
    // Number keys pick a character: to hand over a held item, or to view them
    const slot = parseInt(e.key, 10) - 1;
    if (slot >= 0 && slot < party.length) {
      if (this.props.context.selectedItem) {
        this.props.context.transferItem(slot);
      } else {
        openCharacterSheet(slot);
      }
      return;
    }
    // Arrows and Tab move between the tabs
    const index = TABS.findIndex(([id]) => id === this.state.tab);
    if (e.key === 'ArrowRight' || (e.key === 'Tab' && !e.shiftKey)) {
      e.preventDefault();
      this.setState({ tab: TABS[(index + 1) % TABS.length][0] });
    } else if (e.key === 'ArrowLeft' || (e.key === 'Tab' && e.shiftKey)) {
      e.preventDefault();
      this.setState({ tab: TABS[(index - 1 + TABS.length) % TABS.length][0] });
    }
  }

  // What the footer says depends on whether an item is in hand
  renderHint() {
    const { selectedItem, party } = this.props.context;
    if (selectedItem) {
      const holder = party[selectedItem.index];
      const item = holder && holder.backpack.find((i) => i.id === selectedItem.itemId);
      if (item) {
        return (
          <div className="sheet-hint holding">
            Holding the {itemName(item)} — click a portrait or press 1-6 to hand it over
          </div>
        );
      }
    }
    return <div className="sheet-hint">Press 1-6 or click a portrait to view that character · ← → changes tab</div>;
  }

  renderTabs() {
    return (
      <div className="sheet-tabs">
        {TABS.map(([id, label]) => (
          <div
            key={id}
            className={classNames('sheet-tab', { chosen: this.state.tab === id })}
            onClick={() => this.setState({ tab: id })}
          >
            {label}
          </div>
        ))}
      </div>
    );
  }

  // Worn gear: click a slot to take the item off
  renderEquipment(c, index) {
    const { unequipSlot } = this.props.context;
    return (
      <section className="sheet-gear">
        <h2>Equipped</h2>
        {SLOTS.map((slot) => {
          const item = c.equipment[slot];
          return (
            <div
              key={slot}
              className={classNames('sheet-slot', { empty: !item })}
              onClick={item ? () => unequipSlot(index, slot) : undefined}
              title={item ? 'Click to take off' : undefined}
            >
              <span className="slot-name">{SLOT_NAMES[slot]}</span>
              {item ? (
                <span className="slot-item">{itemName(item)}<span className="slot-detail">{itemSummary(item)}</span></span>
              ) : (
                <span className="sheet-muted">—</span>
              )}
            </div>
          );
        })}
      </section>
    );
  }

  // Carried gear: click an item to pick it up for handing over, WEAR to equip
  renderBackpack(c, index) {
    const { equipItem, dropItem, selectItem, useItem, selectedItem } = this.props.context;
    const held = selectedItem && selectedItem.index === index ? selectedItem.itemId : null;
    return (
      <section className="sheet-gear">
        <h2>Pack ({c.backpack.length}/{BACKPACK_SIZE})</h2>
        {c.backpack.length === 0 && <div className="sheet-muted">Carrying nothing</div>}
        {c.backpack.map((item) => (
          <div key={item.id} className={classNames('sheet-slot', { held: held === item.id })}>
            <span className="slot-item" onClick={() => selectItem(index, item.id)} title="Click, then click a portrait to hand it over">
              {itemName(item)}<span className="slot-detail">{SLOT_NAMES[item.slot] || 'Potion'} · {itemSummary(item)}</span>
            </span>
            {item.slot === 'consumable' ? (
              <span className="slot-action" onClick={() => useItem(index, item.id)} title="Drink this">DRINK</span>
            ) : (
              <span className="slot-action" onClick={() => equipItem(index, item.id)} title="Wear this">WEAR</span>
            )}
            <span className="slot-drop" onClick={() => dropItem(index, item.id)} title="Drop">✕</span>
          </div>
        ))}
      </section>
    );
  }

  renderWeapon(label, weapon, hitBonus, damageBonus) {
    return (
      <div className="sheet-row">
        <span>{label}</span>
        {weapon ? (
          <span>{weapon.name} — to hit {signed(hitBonus)}, damage {damageText(weapon.dice, damageBonus)}</span>
        ) : (
          <span className="sheet-muted">None</span>
        )}
      </div>
    );
  }

  render() {
    const { characterSheet, party, closeCharacterSheet } = this.props.context;
    const c = party[characterSheet];
    if (!c) return null;

    return (
      <div id="characterSheet">
        <div className="sheet-portrait" style={{ backgroundImage: `url('${c.portrait}')` }} />

        <div className="sheet-body">
          <div className="sheet-header">
            <div>
              <h1>{c.name}</h1>
              <div className="sheet-subtitle">Level {c.level} {c.race} {c.className}</div>
            </div>
            <div className="sheet-close" onClick={closeCharacterSheet}>CLOSE (ESC)</div>
          </div>

          {this.renderTabs()}

          {this.state.tab === 'stats' && (
            <div className="sheet-tab-body">
              <div className="sheet-columns">
                <section>
                  <h2>Attributes</h2>
                  {STATS.map(([key, label]) => (
                    <div key={key} className="sheet-row">
                      <span>{label}</span>
                      <span>{c.stats[key]} <span className="sheet-muted">({signed(statBonus(c.stats[key]))})</span></span>
                    </div>
                  ))}
                </section>

                <section>
                  <h2>Status</h2>
                  <div className="sheet-row"><span>Condition</span><span className={'sheet-condition ' + c.condition}>{c.condition.toUpperCase()}</span></div>
                  <div className="sheet-row"><span>Hit Points</span><span>{c.hp} / {c.maxHp}</span></div>
                  <div className="sheet-row"><span>Spell Points</span><span>{c.maxSp > 0 ? `${c.sp} / ${c.maxSp}` : '—'}</span></div>
                  <div className="sheet-row"><span>Armor Class</span><span>{characterAC(c)}</span></div>
                  <div className="sheet-row">
                    <span>Experience</span>
                    <span>
                      {c.xp}{' '}
                      {hasExperienceToTrain(c) ? (
                        <span className="sheet-ready">ready to train</span>
                      ) : (
                        <span className="sheet-muted">({Math.round(levelProgress(c) * 100)}% to level {c.level + 1}, {xpToNext(c)} to go)</span>
                      )}
                    </span>
                  </div>
                </section>
              </div>

              <section>
                <h2>Attacks</h2>
                {this.renderWeapon('Melee', c.weapon, meleeHitBonus(c), statBonus(c.stats.might) + (c.weapon.damageBonus || 0))}
                {this.renderWeapon('Missile', c.missile, c.missile ? missileHitBonus(c) : 0, c.missile ? c.missile.damageBonus || 0 : 0)}
              </section>
            </div>
          )}

          {this.state.tab === 'gear' && (
            <div className="sheet-tab-body">
              <div className="sheet-columns sheet-gear-row">
                {this.renderEquipment(c, characterSheet)}
                {this.renderBackpack(c, characterSheet)}
              </div>
            </div>
          )}

          {this.state.tab === 'spells' && (
            <div className="sheet-tab-body">
              <section>
                <h2>Spells</h2>
                {c.spells.length === 0 && <div className="sheet-muted">Knows no spells</div>}
                {c.spells.map((spellId) => (
                  <div key={spellId} className="sheet-row">
                    <span>{SPELLS[spellId].name} <span className="sheet-muted">({spellCost(spellId, c)} SP)</span></span>
                    <span>{SPELLS[spellId].description}{SPELLS[spellId].target === 'enemy' ? ' — combat only' : ''}</span>
                  </div>
                ))}
              </section>
            </div>
          )}

          {this.renderHint()}
        </div>
      </div>
    );
  }
}

export default withContext(CharacterSheet);
